// Pure classifier for a recipient's per-share delivery state.
//
// Single source of truth for the lifecycle taxonomy used by the
// DCPDeliveryAnalyticsDrawer (and any other surface that needs to
// describe "what state is this delivery in"). Extracted from the
// drawer component so it's import-testable; the drawer imports
// `classifyDownload` and renders chips/banners off the result.
//
// Inputs: a recipient object shaped like the API's
//   /api/dcp-jobs/<id>/delivery-analytics → recipients[] entries.
// Outputs: { key, label, color, detail } where:
//   - key      = stable string for tests / aria-labels
//                ('revoked' | 'delivered_manual' | 'complete' | 'active'
//                 | 'incomplete' | 'stalled' | 'just_started' | 'opened'
//                 | 'sent' | 'created')
//   - label    = short chip-friendly text
//   - color    = MUI color slot ('success' | 'info' | 'warning' | 'error' | 'default')
//   - detail   = sentence-form explanation suitable for an Alert body

export function fmtBytes(b) {
  if (b == null) return '—';
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  return `${(b / 1e3).toFixed(0)} KB`;
}

export function fmtAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return null;
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h ago`;
}

// "Active" state has a 5-minute liveness window — last MinIO activity
// must be within that window to count as in-flight rather than aborted.
export const ACTIVE_WINDOW_MS = 5 * 60_000;
// "Stalled" is registered after 30 minutes from the click with no bytes.
export const STALL_THRESHOLD_MS = 30 * 60_000;

export function classifyDownload(r) {
  const L = r.lifecycle || {};
  const tfs = r.transfers;
  const completedAudit = (r.confirmed_count || 0) > 0 || (tfs && tfs.complete > 0);

  if (L.revoked_at) {
    return {
      key: 'revoked',
      label: 'Revoked',
      color: 'default',
      detail: `Link revoked ${fmtAgo(L.revoked_at)}.`,
    };
  }
  // Operator-confirmed manual delivery wins over MinIO heuristics — the
  // operator told us with certainty that the recipient has the file.
  if (L.manually_confirmed_at) {
    const note = L.manual_confirmation_note ? ` · ${L.manual_confirmation_note}` : '';
    return {
      key: 'delivered_manual',
      label: 'Delivered (manual)',
      color: 'success',
      detail: `Operator-confirmed delivery ${fmtAgo(L.manually_confirmed_at)}${note}.`,
    };
  }
  if (completedAudit) {
    const lastTs = tfs?.last_completed_at;
    const peak = tfs?.peak_mbps ? ` · peak ${tfs.peak_mbps.toFixed(0)} Mbps` : '';
    const totalBytes = tfs?.total_bytes ? ` · ${fmtBytes(tfs.total_bytes)} served` : '';
    return {
      key: 'complete',
      label: 'Delivered',
      color: 'success',
      detail: `MinIO served the full file${tfs?.complete ? ` (${tfs.complete} verified transfer${tfs.complete === 1 ? '' : 's'})` : ''}${totalBytes}${peak}${lastTs ? ` · last ${fmtAgo(lastTs)}` : ''}.`,
    };
  }
  // Transfer rows exist but none completed — active or aborted.
  if (tfs && tfs.count > 0) {
    const lastActivity = tfs.last_completed_at
      || (tfs.rows && tfs.rows[0]?.completed_at)
      || (tfs.rows && tfs.rows[0]?.started_at);
    const ageMs = lastActivity ? Date.now() - new Date(lastActivity).getTime() : Infinity;
    if (ageMs < ACTIVE_WINDOW_MS) {
      return {
        key: 'active',
        label: 'Downloading',
        color: 'info',
        detail: `Transfer in flight · ${tfs.count} request${tfs.count === 1 ? '' : 's'} so far · ${fmtBytes(tfs.total_bytes)} served · last activity ${fmtAgo(lastActivity)}.`,
      };
    }
    return {
      key: 'incomplete',
      label: 'Incomplete',
      color: 'warning',
      detail: `Recipient pulled ${fmtBytes(tfs.total_bytes)} across ${tfs.count} transfer${tfs.count === 1 ? '' : 's'} but none completed. Last activity ${fmtAgo(lastActivity)}. Likely a network drop, browser cancellation, or disk-space issue at the venue. Re-send the link or have them retry.`,
    };
  }
  // Click logged but no MinIO bytes ever flowed — "bounced" download.
  if (L.download_started_at) {
    const ageMs = Date.now() - new Date(L.download_started_at).getTime();
    if (ageMs > STALL_THRESHOLD_MS) {
      return {
        key: 'stalled',
        label: 'No transfer recorded',
        color: 'warning',
        detail: `Recipient clicked Download ${fmtAgo(L.download_started_at)} but no bytes were served from MinIO under this share. They may have closed the tab / hit a filter / never followed through — OR you delivered the package to them out-of-band (drive, FileMail, alternate uplink) and the audit pipeline can't see that. If the recipient confirms they have it, click Mark as delivered below to update the analytics.`,
      };
    }
    return {
      key: 'just_started',
      label: 'Starting',
      color: 'info',
      detail: `Click registered ${fmtAgo(L.download_started_at)} — waiting for the first byte from MinIO.`,
    };
  }
  if (L.opened_at) {
    return {
      key: 'opened',
      label: 'Opened',
      color: 'info',
      detail: `Opened the share landing page ${fmtAgo(L.opened_at)} but hasn't clicked Download.`,
    };
  }
  if (L.email_sent_at) {
    return {
      key: 'sent',
      label: 'Sent',
      color: 'default',
      detail: `Email sent ${fmtAgo(L.email_sent_at)} — recipient hasn't opened the link yet.`,
    };
  }
  return {
    key: 'created',
    label: 'Created',
    color: 'default',
    detail: 'Share created — no email sent yet.',
  };
}
